import type { TFunction } from 'i18next'
import {
  Building2,
  Container,
  Plane,
  Ship,
  TrainFront,
  TriangleAlert,
  Truck,
  type LucideIcon,
} from 'lucide-react'

import type { TransportMode, TransportRef } from '@/types/domain'

/* Lucide is the shadcn default set, adopted deliberately.
 * The brand guideline has its own icon page (p.19) whose drawing style Lucide
 * does not match; the mismatch is accepted in exchange for full coverage and
 * zero drawing work, and brand icons stay correct for marketing surfaces. */

/** Resolves an API-supplied icon name to a component. The API sends names
 *  rather than components, so this is the one place the mapping lives. */
const BY_NAME: Record<string, LucideIcon> = {
  'building-2': Building2,
  container: Container,
  plane: Plane,
  ship: Ship,
  'train-front': TrainFront,
  'triangle-alert': TriangleAlert,
  truck: Truck,
}

export function iconByName(name: string | undefined): LucideIcon | undefined {
  return name ? BY_NAME[name] : undefined
}

export const MODE_ICONS: Record<TransportMode, LucideIcon> = {
  sea: Ship,
  rail: TrainFront,
  road: Truck,
  air: Plane,
  multimodal: Container,
}

/** The `modes` catalogue key for a transport mode. Translate at the point of
 *  render: `t(modeLabelKey(row.mode))`. A baked-in string here would not follow
 *  a language switch. */
export function modeLabelKey(mode: TransportMode): `modes.${TransportMode}` {
  return `modes.${mode}`
}

/** The wire's directory code ("RAIL", "MULTI" — directory transport_refs) as
 *  the domain mode, or undefined when it is unset or unrecognised.
 *
 *  The server serves the CODE and the client maps it to its own vocabulary
 *  (0003's rule for wire values); this is the one place that mapping lives, so
 *  quotes and cases cannot translate the same code two ways. Undefined rather
 *  than a guess for the unknown case — what to render then is the caller's
 *  question: a quote card drops the mode, a case list falls back to the
 *  container icon. */
const MODE_BY_CODE: Record<string, TransportMode> = {
  sea: 'sea',
  rail: 'rail',
  road: 'road',
  air: 'air',
  multi: 'multimodal',
  multimodal: 'multimodal',
}

export function modeFromCode(code: string | undefined): TransportMode | undefined {
  if (!code) return undefined
  const key = code.toLowerCase()
  // Own keys only: the desk types codes, and 'constructor' is not a mode.
  return Object.hasOwn(MODE_BY_CODE, key) ? MODE_BY_CODE[key] : undefined
}

/** How a transport-mode directory row is named in a picker (QW-1).
 *
 *  The directory seeds its modes in English ('Rail', 'Sea'), so its label is
 *  the server's words, not the reader's. A row whose CODE this client knows
 *  is named by the catalogue — the code is the stable identity, and a renamed
 *  'Rail' is still rail. A row the desk added under a code no key covers
 *  (FR-43) keeps its own label, which is the only name it has. */
export function transportModeLabel(
  ref: Pick<TransportRef, 'code' | 'label'>,
  t: TFunction,
): string {
  const mode = modeFromCode(ref.code)
  return mode ? t(modeLabelKey(mode)) : ref.label
}
